import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { fmtUSD } from "../lib/format";
import { currentNepalMonth } from "../lib/partyBook";
import {
  ChevronLeft,
  ChevronRight,
  Coins,
  Settings2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const monthAdd = (ym, n) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const currentMonth = currentNepalMonth;
const monthLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const fmtUSDT = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });
const fmtNPR = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
const fmtPoints = (n) =>
  `${Number(n || 0).toLocaleString("en-US", {
    maximumFractionDigits: 4,
  })} pts`;
const SALARY_STRUCTURE_V2_START_MONTH = "2026-09";
const ROLLING_COIN_STOCK_START_MONTH = "2026-10";

export default function NetProfits() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [rateOpen, setRateOpen] = useState(false);
  const [draftUsd, setDraftUsd] = useState("");
  const [draftNpr, setDraftNpr] = useState("");

  const [showTeams, setShowTeams] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/net-profits", { params: { month } });
      setData(r.data);
      setDraftUsd(String(r.data?.settings?.usd_to_usdt ?? 1));
      setDraftNpr(String(r.data?.settings?.npr_to_usdt ?? 138));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [month]);

  const saveRates = async () => {
    const u = Number(draftUsd);
    const n = Number(draftNpr);
    if (!u || u <= 0) return toast.error("USD→USDT rate must be positive");
    if (!n || n <= 0) return toast.error("NPR→USDT rate must be positive");
    try {
      await api.put(`/salary-settings/${month}`, {
        usd_to_usdt: u,
        npr_to_usdt: n,
      });
      toast.success("Rates saved");
      setRateOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const t = data?.totals || {};
  const s = data?.settings || {};
  const usdRate = s.usd_to_usdt ?? 1;
  const nprRate = s.npr_to_usdt ?? 138;
  const fixedStaffMode = month >= SALARY_STRUCTURE_V2_START_MONTH;
  const rollingCoinMode = month >= ROLLING_COIN_STOCK_START_MONTH;

  return (
    <Layout
      title="Net Profits"
      subtitle="USDT-canonical · USD subtotal converted, NPR payouts converted, USDT expenses deducted"
      testId="net-profits-page"
      actions={
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <Button
            variant="outline"
            onClick={() => setRateOpen(true)}
            className="h-9"
            data-testid="set-rate-btn"
          >
            <Settings2 size={14} className="mr-1.5" /> Rates · ×{usdRate} / ÷{nprRate}
          </Button>
          <Link to="/expenses">
            <Button
              variant="outline"
              className="h-9"
              data-testid="manage-expenses-btn"
            >
              <ExternalLink size={14} className="mr-1.5" /> Manage expenses
            </Button>
          </Link>
          <Link to="/net-profit-analytics">
            <Button
              className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="np-analytics-btn"
            >
              Analytics →
            </Button>
          </Link>
        </div>
      }
    >
      {/* Month nav */}
      <div className="stat-card p-3 mb-5 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setMonth(monthAdd(month, -1))}
          className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center"
          data-testid="np-prev-month"
        >
          <ChevronLeft size={14} strokeWidth={1.7} />
        </button>
        <div
          className="font-display text-[15px] font-semibold tracking-tight px-2"
          data-testid="np-current-month"
        >
          {monthLabel(month)}
        </div>
        <button
          onClick={() => setMonth(monthAdd(month, 1))}
          className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center"
          data-testid="np-next-month"
        >
          <ChevronRight size={14} strokeWidth={1.7} />
        </button>
        {month !== currentMonth() && (
          <button
            onClick={() => setMonth(currentMonth())}
            className="h-8 px-3 rounded-md text-[12.5px] border hairline bg-white hover:bg-zinc-50 ml-1"
            data-testid="np-this-month"
          >
            This month
          </button>
        )}
        <span className="ml-auto text-[11.5px] text-zinc-500 font-mono">
          1 USD = {usdRate} USDT · 1 USDT = {nprRate} NPR
        </span>
      </div>

      {loading || !data ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <>
          {/* Top stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            <Stat
              label="Gross (USD)"
              value={fmtUSD(t.gross_usd)}
              sub={
                fixedStaffMode
                  ? `Used ${fmtPoints(t.net_coin_points)} · coin cut ${fmtUSD(t.coin_usd)}`
                  : `Coin ${fmtUSD(t.coin_usd)}`
              }
            />
            <Stat
              label="Subtotal (USDT)"
              value={`${fmtUSDT(t.subtotal_usdt)}`}
              sub={
                fixedStaffMode
                  ? `= (Gross − PG − Dispute − 16% coin) × ${usdRate}`
                  : `= (Gross − PG − Dispute − Coin) × ${usdRate}`
              }
              accent="emerald"
            />
            <Stat
              label={fixedStaffMode ? "Team payouts" : "Team / Mgr / CF"}
              value={fmtUSDT(
                (t.team_payout_usdt || 0) +
                  (fixedStaffMode ? 0 : (t.manager_payout_usdt || 0) + (t.cf_payout_usdt || 0))
              )}
              sub={`${fmtNPR(
                (t.team_payout_npr || 0) +
                  (fixedStaffMode ? 0 : (t.manager_payout_npr || 0) + (t.cf_payout_npr || 0))
              )} NPR`}
              accent="rose"
            />
            <Stat
              label="Bonuses"
              value={fmtUSDT(t.bonus_total_usdt)}
              sub={`${fmtNPR(t.bonus_total_npr)} NPR`}
              accent="rose"
            />
            <Stat
              label={fixedStaffMode ? "Staff salaries" : "Others"}
              value={fmtUSDT(
                fixedStaffMode ? t.staff_salaries_total_usdt : t.others_total_usdt
              )}
              sub={`${fmtNPR(
                fixedStaffMode ? t.staff_salaries_total_npr : t.others_total_npr
              )} NPR`}
              accent="rose"
            />
            <button
              type="button"
              onClick={() => setShowTeams(true)}
              data-testid="net-profit-final-card"
              className="stat-card p-4 text-left transition-shadow hover:shadow-md ring-1 ring-emerald-100 hover:border-emerald-300"
            >
              <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
                Final net (USDT)
              </div>
              <div
                className={`font-mono text-[22px] font-medium mt-1 ${
                  (t.final_net_usdt || 0) >= 0
                    ? "text-emerald-700"
                    : "text-rose-700"
                }`}
              >
                {fmtUSDT(t.final_net_usdt)}
              </div>
              <div className="text-[10.5px] text-zinc-500 mt-0.5 font-mono">
                ≈ {fmtUSD(t.final_net_usd)}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                Click for per-team →
              </div>
            </button>
          </div>

          {/* Per-team contribution table */}
          <div className="stat-card overflow-hidden mb-5">
            <div className="p-4 border-b hairline">
              <div className="font-display font-bold tracking-tight">
                Per-team contribution · {monthLabel(month)}
              </div>
              <div className="text-[11.5px] text-zinc-500 mt-0.5">
                {fixedStaffMode
                  ? rollingCoinMode
                    ? "Used Coin = stock bridge, except Tora / Bulls / FIN use Direct Net Coin · coin cut is 16%"
                    : "Used Coin = max(Loaded − Stock, 0) · coin cut is 16% of Used Coin"
                  : "Gross − PG − Dispute − Coin, then payouts (all in USDT)"}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                    <th className="py-3 px-4 font-medium">Team</th>
                    <th className="py-3 px-4 font-medium text-right">Gross</th>
                    <th className="py-3 px-4 font-medium text-right">PG</th>
                    <th className="py-3 px-4 font-medium text-right">Dispute</th>
                    {fixedStaffMode && (
                      <>
                        {rollingCoinMode && (
                          <th className="py-3 px-4 font-medium text-right">Opening pts</th>
                        )}
                        <th className="py-3 px-4 font-medium text-right">Loaded pts</th>
                        <th className="py-3 px-4 font-medium text-right">Closing pts</th>
                        <th className="py-3 px-4 font-medium text-right">Used pts</th>
                      </>
                    )}
                    <th className="py-3 px-4 font-medium text-right">
                      {fixedStaffMode ? "Coin cut 16%" : "Coin"}
                    </th>
                    <th className="py-3 px-4 font-medium text-right">
                      Subtotal (USD)
                    </th>
                    <th className="py-3 px-4 font-medium text-right">
                      Subtotal (USDT)
                    </th>
                    <th className="py-3 px-4 font-medium text-right">Team</th>
                    {!fixedStaffMode && (
                      <th className="py-3 px-4 font-medium text-right">Mgr</th>
                    )}
                    {!fixedStaffMode && (
                      <th className="py-3 px-4 font-medium text-right">CF</th>
                    )}
                    <th className="py-3 px-4 font-medium text-right">Bonus</th>
                    <th className="py-3 px-4 font-medium text-right">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y hairline">
                  {(data.per_team || []).length === 0 ? (
                    <tr>
                      <td
                        colSpan={rollingCoinMode ? 14 : 13}
                        className="py-8 text-center text-zinc-500"
                      >
                        No team sheets for this month.
                      </td>
                    </tr>
                  ) : (
                    (data.per_team || []).map((p) => (
                      <tr
                        key={p.team_name}
                        className="row-hover"
                        data-testid={`np-team-row-${p.team_name}`}
                      >
                        <td className="py-3 px-4 font-medium">
                          {p.team_name}
                          {p.coin_entry_mode === "direct" && (
                            <div className="mt-0.5 text-[10px] font-normal text-amber-700">
                              Direct Net Coin
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono text-right">
                          {fmtUSD(p.gross_usd)}
                        </td>
                        <td className="py-3 px-4 font-mono text-right text-zinc-500">
                          {fmtUSD(p.pg_usd)}
                        </td>
                        <td className="py-3 px-4 font-mono text-right text-rose-700">
                          {fmtUSD(p.dispute_usd)}
                        </td>
                        {fixedStaffMode && (
                          <>
                            {rollingCoinMode && (
                              <td className="py-3 px-4 font-mono text-right text-indigo-700">
                                {p.coin_entry_mode === "direct"
                                  ? "—"
                                  : fmtPoints(p.opening_stock)}
                              </td>
                            )}
                            <td className="py-3 px-4 font-mono text-right text-zinc-500">
                              {p.coin_entry_mode === "direct"
                                ? "—"
                                : fmtPoints(p.total_loaded)}
                            </td>
                            <td className="py-3 px-4 font-mono text-right text-zinc-500">
                              {p.coin_entry_mode === "direct"
                                ? "—"
                                : fmtPoints(p.team_stock)}
                            </td>
                            <td className="py-3 px-4 font-mono text-right text-amber-700">
                              {fmtPoints(p.net_coin_used)}
                            </td>
                          </>
                        )}
                        <td className="py-3 px-4 font-mono text-right text-rose-700">
                          {fmtUSD(p.coin_usd)}
                        </td>
                        <td className="py-3 px-4 font-mono text-right">
                          {fmtUSD(p.subtotal_usd)}
                        </td>
                        <td className="py-3 px-4 font-mono text-right text-emerald-700">
                          {fmtUSDT(p.subtotal_usdt)}
                        </td>
                        <td className="py-3 px-4 font-mono text-right text-rose-700">
                          {fmtUSDT(p.team_payout_usdt)}
                          <div className="text-[10px] text-zinc-400">
                            {fmtNPR(p.team_payout_npr)} NPR
                          </div>
                        </td>
                        {!fixedStaffMode && (
                          <td className="py-3 px-4 font-mono text-right text-rose-700">
                            {fmtUSDT(p.manager_payout_usdt)}
                            <div className="text-[10px] text-zinc-400">
                              {fmtNPR(p.manager_payout_npr)} NPR
                            </div>
                          </td>
                        )}
                        {!fixedStaffMode && (
                          <td className="py-3 px-4 font-mono text-right text-rose-700">
                            {fmtUSDT(p.cf_payout_usdt)}
                            <div className="text-[10px] text-zinc-400">
                              {fmtNPR(p.cf_payout_npr)} NPR
                            </div>
                          </td>
                        )}
                        <td className="py-3 px-4 font-mono text-right text-rose-700">
                          {fmtUSDT(p.bonus_total_usdt)}
                        </td>
                        <td
                          className={`py-3 px-4 font-mono text-right font-semibold ${
                            p.team_net_usdt >= 0
                              ? "text-emerald-700"
                              : "text-rose-700"
                          }`}
                        >
                          {fmtUSDT(p.team_net_usdt)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-50/60">
                    <td className="py-2 px-4 text-[10.5px] uppercase tracking-widest text-zinc-500">
                      Subtotal (teams)
                    </td>
                    <td colSpan={rollingCoinMode ? 12 : fixedStaffMode ? 11 : 10}></td>
                    <td className="py-2 px-4 font-mono text-right font-semibold text-emerald-700">
                      {fmtUSDT(t.teams_net_subtotal_usdt)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* USDT expense list (read-only — managed in Expenses page) */}
          <div className="stat-card overflow-hidden mb-5">
            <div className="p-4 border-b hairline flex items-center justify-between">
              <div>
                <div className="font-display font-bold tracking-tight flex items-center gap-1.5">
                  <Coins size={14} strokeWidth={1.7} className="text-amber-600" />
                  USDT expenses · {monthLabel(month)}
                </div>
                <div className="text-[11.5px] text-zinc-500 mt-0.5">
                  Pulled from the Expenses module · deducted directly in USDT
                </div>
              </div>
              <span className="font-mono text-[13px]">
                {fmtUSDT(t.usdt_expense_total)} USDT
              </span>
            </div>
            {(data.usdt_expenses || []).length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-[13px]">
                No expenses recorded for this month.{" "}
                <Link
                  to="/expenses"
                  className="underline hover:text-zinc-900"
                  data-testid="np-empty-go-to-expenses"
                >
                  Add one in Expenses →
                </Link>
              </div>
            ) : (
              <table className="min-w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                    <th className="py-2.5 px-4">Date</th>
                    <th className="py-2.5 px-4">Source</th>
                    <th className="py-2.5 px-4">Description</th>
                    <th className="py-2.5 px-4 text-right">USDT</th>
                  </tr>
                </thead>
                <tbody className="divide-y hairline">
                  {data.usdt_expenses.map((x) => (
                    <tr
                      key={x.id}
                      className="row-hover"
                      data-testid={`np-usdt-expense-${x.id}`}
                    >
                      <td className="py-2.5 px-4 font-mono text-[12px] text-zinc-500">
                        {x.date || "—"}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className="inline-flex text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-zinc-50 border-zinc-200 text-zinc-600">
                          {x.source || "—"}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 font-medium">
                        {x.description}
                        {x.notes && (
                          <div className="text-[11px] text-zinc-500">
                            {x.notes}
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 px-4 font-mono text-right text-amber-700">
                        {fmtUSDT(x.amount_usdt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Per-team breakdown dialog */}
      <Dialog open={showTeams} onOpenChange={setShowTeams}>
        <DialogContent
          className="max-w-xl"
          data-testid="net-profit-by-team-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              Net profit by team · {monthLabel(month)}
            </DialogTitle>
            <DialogDescription>
              Each team's contribution after its own distributions. {fixedStaffMode ? "Staff salaries" : "Others"} and
              USDT expenses are applied at the company level.
            </DialogDescription>
          </DialogHeader>
          {(data?.per_team || []).length === 0 ? (
            <div className="text-[13px] text-zinc-500 py-4 text-center">
              No team sheets.
            </div>
          ) : (
            <div className="rounded-md border hairline overflow-hidden">
              <table className="min-w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                    <th className="py-2 px-3">Team</th>
                    <th className="py-2 px-3 text-right">Net (USDT)</th>
                    <th className="py-2 px-3 text-right">% of subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y hairline">
                  {(data.per_team || []).map((p) => {
                    const sub = t.teams_net_subtotal_usdt || 0;
                    const pct = sub
                      ? Math.round((p.team_net_usdt / sub) * 1000) / 10
                      : 0;
                    return (
                      <tr key={p.team_name}>
                        <td className="py-2 px-3 font-medium">
                          {p.team_name}
                        </td>
                        <td
                          className={`py-2 px-3 font-mono text-right ${
                            p.team_net_usdt >= 0
                              ? "text-emerald-700"
                              : "text-rose-700"
                          }`}
                        >
                          {fmtUSDT(p.team_net_usdt)}
                        </td>
                        <td className="py-2 px-3 font-mono text-right">
                          {pct}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-50/60">
                    <td className="py-2 px-3 text-[10.5px] uppercase tracking-widest text-zinc-500">
                      Subtotal
                    </td>
                    <td className="py-2 px-3 font-mono text-right font-semibold">
                      {fmtUSDT(t.teams_net_subtotal_usdt)}
                    </td>
                    <td className="py-2 px-3 font-mono text-right text-zinc-500">
                      100%
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 text-zinc-500">
                      − {fixedStaffMode ? "Staff salaries" : "Others"} ({fmtUSDT(
                        fixedStaffMode
                          ? t.staff_salaries_total_usdt
                          : t.others_total_usdt
                      )} USDT)
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                  {fixedStaffMode && (t.others_total_usdt || 0) > 0 && (
                    <tr>
                      <td className="py-2 px-3 text-zinc-500">
                        − Legacy others ({fmtUSDT(t.others_total_usdt)} USDT)
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  )}
                  <tr>
                    <td className="py-2 px-3 text-zinc-500">
                      − USDT expenses ({fmtUSDT(t.usdt_expense_total)})
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                  <tr className="bg-emerald-50/40">
                    <td className="py-2 px-3 text-[10.5px] uppercase tracking-widest text-emerald-700">
                      Final net
                    </td>
                    <td
                      className={`py-2 px-3 font-mono text-right font-semibold ${
                        (t.final_net_usdt || 0) >= 0
                          ? "text-emerald-700"
                          : "text-rose-700"
                      }`}
                    >
                      {fmtUSDT(t.final_net_usdt)}
                    </td>
                    <td className="py-2 px-3 font-mono text-right text-zinc-500">
                      ≈ {fmtUSD(t.final_net_usd)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rates dialog */}
      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent data-testid="usdt-rate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Conversion rates · {monthLabel(month)}
            </DialogTitle>
            <DialogDescription>
              Both sides converge to USDT. <b>USD→USDT</b> applied to (Gross −
              Coin). <b>NPR→USDT</b> applied to {fixedStaffMode
                ? "team payouts, fixed staff salaries and bonuses"
                : "team, manager, CF, bonuses and others"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>1 USD = ? USDT</Label>
              <Input
                type="number"
                step="0.0001"
                value={draftUsd}
                onChange={(e) => setDraftUsd(e.target.value)}
                data-testid="usd-to-usdt-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>1 USDT = ? NPR</Label>
              <Input
                type="number"
                step="0.0001"
                value={draftNpr}
                onChange={(e) => setDraftNpr(e.target.value)}
                data-testid="npr-to-usdt-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveRates}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-usdt-rate-btn"
            >
              Save rates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Stat({ label, value, sub, accent }) {
  const cls =
    {
      emerald: "text-emerald-700",
      rose: "text-rose-700",
      amber: "text-amber-700",
    }[accent || ""] || "text-zinc-900";
  return (
    <div className="stat-card p-4">
      <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <div className={`font-mono text-[20px] font-medium mt-1 ${cls}`}>
        {value}
      </div>
      {sub && (
        <div className="text-[10.5px] text-zinc-500 mt-0.5 font-mono">
          {sub}
        </div>
      )}
    </div>
  );
}
